/**
 * Fallback de rasterização de PDFs que o pdf-lib não consegue ler diretamente.
 *
 * Motivação: o pdf-merger-js usa o pdf-lib internamente, e o pdf-lib NÃO
 * implementa descriptografia. PDFs criptografados (mesmo que abram sem pedir
 * senha — criptografia só de permissões, com senha de usuário vazia) fazem o
 * merge estourar com "Expected instance of PDFDict, but got instance of
 * undefined" ao tentar resolver a árvore de páginas.
 *
 * O pdf.js (pdfjs-dist), por outro lado, descriptografa esses PDFs. Aqui usamos
 * o pdf.js para renderizar cada página em um <canvas> e o pdf-lib para
 * re-empacotar essas imagens como um novo PDF, que então pode ser mesclado
 * normalmente.
 *
 * Limitações: o resultado é rasterizado (sem texto selecionável e com arquivo
 * maior). Por isso é usado apenas como fallback, quando o merge direto falha.
 */
import { PDFDocument } from "pdf-lib";

// Carregado sob demanda (dynamic import) para não pesar no bundle principal:
// o pdf.js só é baixado quando aparece um PDF que precisa do fallback.
let pdfjsPromise: Promise<any> | null = null;

async function getPdfjs(): Promise<any> {
  if (!pdfjsPromise) {
    pdfjsPromise = import(
      // build "legacy" é o mais compatível com bundlers (CRA/webpack)
      /* webpackChunkName: "pdfjs" */ "pdfjs-dist/legacy/build/pdf" as any
    ).then((pdfjsLib: any) => {
      // worker via CDN, casado com a versão instalada, para evitar
      // configuração específica de bundler/worker.
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
      return pdfjsLib;
    });
  }
  return pdfjsPromise;
}

export interface RasterizeOptions {
  /** Escala de renderização. Maior = mais nitidez e arquivo maior. Padrão: 2. */
  scale?: number;
  /** Qualidade do JPEG (0..1). Padrão: 0.85. */
  quality?: number;
}

/**
 * Rasteriza um PDF página a página com pdf.js e devolve um novo PDF (de imagens)
 * como Uint8Array, pronto para ser passado a `merger.add(...)`.
 *
 * @throws se o PDF exigir senha (não vazia) ou não puder ser lido pelo pdf.js.
 */
export async function rasterizeEncryptedPdf(
  data: ArrayBuffer | Uint8Array,
  options?: RasterizeOptions
): Promise<Uint8Array> {
  const scale = options?.scale ?? 2;
  const quality = options?.quality ?? 0.85;

  const pdfjsLib = await getPdfjs();

  const loadingTask = pdfjsLib.getDocument({
    data: data instanceof Uint8Array ? data : new Uint8Array(data),
    // tenta abrir com senha vazia (caso comum: criptografia só de permissões)
    password: "",
  });

  const pdfDoc = await loadingTask.promise;
  const outDoc = await PDFDocument.create();

  try {
    for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("Não foi possível obter o contexto 2D do canvas");
      }

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);

      await page.render({ canvasContext: context, viewport }).promise;

      const jpegDataUrl = canvas.toDataURL("image/jpeg", quality);
      const jpegImage = await outDoc.embedJpg(jpegDataUrl);

      // dimensões sem escala para manter o tamanho original da página (em pt)
      const unscaled = page.getViewport({ scale: 1 });
      const outPage = outDoc.addPage([unscaled.width, unscaled.height]);
      outPage.drawImage(jpegImage, {
        x: 0,
        y: 0,
        width: unscaled.width,
        height: unscaled.height,
      });

      page.cleanup();
      // libera memória do canvas
      canvas.width = 0;
      canvas.height = 0;
    }
  } finally {
    try {
      await pdfDoc.cleanup();
      await loadingTask.destroy?.();
    } catch {
      // ignore cleanup errors
    }
  }

  return await outDoc.save();
}

/**
 * Adiciona um PDF ao merger, com fallback automático de rasterização caso o
 * pdf-lib não consiga lê-lo diretamente (ex.: PDF criptografado).
 *
 * Lança o erro original somente se o fallback também falhar.
 */
export async function addPdfToMergerWithFallback(
  merger: { add: (input: any) => Promise<void> },
  blob: Blob
): Promise<void> {
  try {
    await merger.add(blob);
  } catch (err) {
    const arrayBuffer = await blob.arrayBuffer();
    const rasterized = await rasterizeEncryptedPdf(arrayBuffer);
    await merger.add(rasterized);
  }
}
